import React, { useState, useEffect } from 'react'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import useAuth from '../hooks/useAuth'
import { toast } from 'sonner'
import ContactInformationSection from './ProfileForm/ContactInformationSection'
import AddressSection from './ProfileForm/AddressSection'
import GPDetailsSection from './ProfileForm/GPDetailsSection'
import MedicalHistorySection from './ProfileForm/MedicalHistorySection'

const DependantForm = ({
  setShowForm,
  mode = 'add',
  initialData = {},
  dependant = null,
  dependantId = null,
  setDependant=null,
}) => {
  const axios = useAxiosPrivate()
  const { auth, setAuth, setShowLoader } = useAuth()

  // Dependant-specific fields
  const [dependantData, setDependantData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
  })

  // Prefilled info
  const [profileData, setProfileData] = useState(null)
  const [userInfo, setUserInfo] = useState(null)

  // GP
  const [selectedGP, setSelectedGP] = useState(null)
  const [showGpMap, setShowGpMap] = useState(false)
  const [enterDetailsManually, setEnterDetailsManually] = useState(false)
  const [isNotRegisteredWithGP, setIsNotRegisteredWithGP] = useState(false)
  const [manualDetails, setManualDetails] = useState({
    name: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    phone: '',
    email: '',
  })
  const [gpCleared, setGpCleared] = useState(false)

  // Utility to safely map API address to internal structure
  const safeAddress = (addr) => (
    {
    addressLine1: addr?.addressLine1 ?? '',
    addressLine2: addr?.addressLine2 ?? '',
    city: addr?.city ?? '',
    postcode: addr?.postcode ??  '',
    country: addr?.countryCode ?? 'United Kingdom',
  })

  // Map dependant JSON attributes to form fields
  const mapDependantToState = (data) => {
    const attrs = data || {}
    return {
      dependantFields: {
        first_name: attrs.name.split(' ')[0] || '',
        last_name: attrs.name.split(' ')[1] || '',
        date_of_birth: attrs.date_of_birth || '',
        gender: attrs.user_info.gender || '',
      },
      profile: {
        first_name: attrs.first_name || '',
        last_name: attrs.last_name || '',
        email: dependant ? (attrs?.user_info?.contact_email || '') : (attrs.email || ''),
        phone_number: attrs?.phone_number || '',
        date_of_birth: attrs?.date_of_birth
          ? {
              day: attrs.date_of_birth.split('-')[2],
              month: attrs.date_of_birth.split('-')[1],
              year: attrs.date_of_birth.split('-')[0],
            }
          : { day: '', month: '', year: '' },
        profile_pic: '',
        profile_colour: '',
        background_pic: '',
      },
      user: {
        gender: attrs.gender || '',
        address: safeAddress(attrs?.user_info.address || {}),
        gp: attrs?.user_info?.gp?.name?.trim() ? attrs?.user_info?.gp : [],
        medicalHistory: attrs?.user_info?.medicalHistory,
      },
    }
  }


   // Prefill form if editing
   useEffect(() => {
    const data = dependant || initialData
    if (mode === 'edit' && data) {
      const mapped = mapDependantToState(data)
      setDependantData(mapped.dependantFields)
      setProfileData(mapped.profile)
      setUserInfo(mapped.user)
      if (mapped.user.gp) setSelectedGP(mapped.user.gp)
    } else if (auth?.user) {
      setProfileData({
        first_name: '',
        last_name: '',
        email: auth.user.email || '',
        phone_number: auth.user.phone_number || '',
        date_of_birth: { day: '', month: '', year: '' },
        profile_pic: '',
        profile_colour: '',
        background_pic: '',
      })

      setUserInfo({
        gender: '',
        country: auth.user.user_info?.country || 'United Kingdom',
        language: auth.user.user_info?.language || 'English',
        address: safeAddress(auth.user.user_info?.address),
        gp: {
          ...auth.user.user_info?.gp,
          address: safeAddress(auth.user.user_info?.gp?.address),
        },
        medicalHistory: {
          allergies: { preferNotToSay: false, noKnownAllergies: false, value: '' },
          medications: { takingNone: false, preferNotToSay: false, value: '' },
          conditions: { noKnownConditions: false, preferNotToSay: false, value: '' },
        },
      })
    }
  }, [mode, dependantId, auth?.user])


  const handleChange = (e) => {
    const { name, value } = e.target
    setDependantData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async () => {
    setShowLoader(true)
    try {
      let gpData = selectedGP || manualDetails || userInfo.gp
      if (enterDetailsManually) {
        gpData = {
          name: manualDetails.name || '',
          email: manualDetails.email || '',
          phone: manualDetails.phone || '',
          address: {
            city: manualDetails.city || '',
            country: manualDetails.country || '',
            addressLine1: manualDetails.addressLine1 || '',
            addressLine2: manualDetails.addressLine2 || '',
            postcode: manualDetails.postcode || '',
          }
        }
      }
      let addressData = userInfo.address

      let formattedDependantData = {
        name: dependantData.first_name + ' ' + dependantData.last_name,
        date_of_birth: dependantData.date_of_birth,
        gender: dependantData.gender,
        contact_email: profileData?.email,
        phone_number: profileData?.phone_number,
      }

      const formData = {
        ...formattedDependantData,
        address: addressData,
        gp: gpData,
        medicalHistory: userInfo.medicalHistory,
      }

      const endpoint =
        mode === 'edit'
          ? `/users/${auth.user.id}/dependants/${dependant?.id || dependantId}`
          : `/users/${auth.user.id}/dependants`
      const method = mode === 'edit' ? axios.put : axios.post

      const response = await method(endpoint, formData)

      if (!response.data?.error) {
        toast.success(
          mode === 'edit'
            ? 'Dependant updated successfully!'
            : 'Dependant added successfully!'
        )

        let curDeps = auth?.user?.dependants
        curDeps[Object.keys(auth?.user?.dependants).find((key) => auth?.user?.dependants[key].id === dependant.id)] = response?.data?.result
        await setAuth((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            dependants: curDeps
          },
        }))
        setShowForm(false)
        setShowLoader(false)
      } else {
        toast.error(response.data?.message || 'Something went wrong.')
      }
    } catch (error) {
      setShowLoader(false)
      console.error('Form submission failed:', error)
      toast.error(error.response?.data?.message || 'Something went wrong.')
    } finally {
      setShowLoader(false)
    }
  }
  
  const handleClearSelectedGP = async () => {
    setSelectedGP(null)
    setEnterDetailsManually(false)
    setGpCleared(true)
    setManualDetails({
      name: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      postcode: '',
      country: 'United Kingdom',
      phone: '',
      email: '',
    })
    setIsNotRegisteredWithGP(false)
    setShowGpMap(false)

    // Update profile immediately to clear GP data
    try {
      const updatedUserInfo = {
        ...userInfo,
        gp: null,
      }
      setUserInfo(updatedUserInfo)

      toast.success('GP details cleared successfully!')
    } catch (error) {
      setShowLoader(false)
      console.error('Error clearing GP details:', error)
      toast.error('Failed to clear GP details. Please try again.')
    }
  }

  const handleEditSelectedGP = () => {
    if (!selectedGP) return
    setEnterDetailsManually(true)
    setGpCleared(false)
    let line1, line2, town, country
    if (typeof selectedGP?.address === 'string') {
      const parts = selectedGP.address.split(',').map((part) => part.trim())
      line1 = parts[0] || ''
      line2 = parts[1] || ''
      town = parts[2] || ''
      country = parts[3] || ''
    } else if (typeof selectedGP?.address === 'object') {
      line1 = selectedGP?.address['addressLine1'] || ''
      line2 = selectedGP?.address['addressLine2'] || ''
      town = selectedGP?.address['city'] || ''
      country = selectedGP?.address['country'] || ''
    }
    setManualDetails({
      name: selectedGP.name || selectedGP.surgeryName || '',
      addressLine1: line1 || '',
      addressLine2: line2 || '',
      city: town || '',
      postcode: selectedGP.address?.postcode || selectedGP.postcode || '',
      country: selectedGP.country ?? country ?? '',
      phone: selectedGP.phone || '',
      email: selectedGP.email || '',
    })
  }

  return (
    <div className="w-full mx-auto bg-[#f8f6fb] rounded-xl p-6">
      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-[#41264B]">
            {mode === 'edit' ? 'Edit Dependant' : 'Add Dependant'}
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            {mode === 'edit'
              ? 'Update the dependant’s details below.'
              : 'Complete the form below to add a dependant.'}
          </p>
        </div>
      </div>

      {/* Dependant Fields */}
      <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
        <div className="sm:col-span-3">
          <label className="block text-sm font-medium text-gray-700">First Name</label>
          <input
            type="text"
            name="first_name"
            value={dependantData.first_name}
            onChange={handleChange}
            placeholder="First name"
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-1.5"
          />
        </div>

        <div className="sm:col-span-3">
          <label className="block text-sm font-medium text-gray-700">Last Name</label>
          <input
            type="text"
            name="last_name"
            value={dependantData.last_name}
            onChange={handleChange}
            placeholder="Last name"
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-1.5"
          />
        </div>

        <div className="sm:col-span-3">
          <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
          <input
            type="date"
            name="date_of_birth"
            value={dependantData.date_of_birth}
            onChange={handleChange}
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-1.5"
          />
        </div>
{/* 
        <div className="sm:col-span-3">
          <label className="block text-sm font-medium text-gray-700">Relationship</label>
          <select
            name="relationship"
            value={dependantData.relationship}
            onChange={handleChange}
            className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-1.5 bg-white"
          >
            <option value="">Select relationship</option>
            <option value="Brother">Brother</option>
            <option value="Daughter">Daughter</option>
            <option value="Father">Father</option>
            <option value="Friend">Friend</option>
            <option value="Mother">Mother</option>
            <option value="Son">Son</option>
            <option value="Spouse">Spouse</option>
            <option value="Wife">Wife</option>
            <option value="Husband">Husband</option>
            <option value="Other">Other</option>
          </select>
        </div> */}

        <div className="sm:col-span-6">
          <h2 className="font-bold mb-2">Sex Assigned at Birth</h2>
          <p className="text-sm text-gray-400">
            This refers to your dependant’s sex at birth.
          </p>
          <div className="mt-3 flex gap-6">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="gender"
                value="Male"
                checked={dependantData.gender === 'Male'}
                onChange={handleChange}
                className="h-4 w-4 text-violet-500"
              />
              <span className="text-sm text-gray-700">Male</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="gender"
                value="Female"
                checked={dependantData.gender === 'Female'}
                onChange={handleChange}
                className="h-4 w-4 text-violet-500"
              />
              <span className="text-sm text-gray-700">Female</span>
            </label>
          </div>
        </div>
      </div>

      {/* Shared Sections */}
      <div className="grid grid-cols-1 gap-y-6 py-8 sm:grid-cols-6 sm:gap-x-6">
        <ContactInformationSection
          profileData={profileData}
          setProfileData={setProfileData}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          dependant={true}
        />
        <AddressSection
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          profileData={profileData}
          setProfileData={setProfileData}
          showTitle={false}
        />
      </div>

      {/* GP Section */}
      <div className="grid grid-cols-1 gap-y-6 py-8 sm:grid-cols-6 sm:gap-x-6">
        <GPDetailsSection
          selectedGP={selectedGP}
          setSelectedGP={setSelectedGP}
          showGpMap={showGpMap}
          setShowGpMap={setShowGpMap}
          enterDetailsManually={enterDetailsManually}
          setEnterDetailsManually={setEnterDetailsManually}
          manualDetails={manualDetails}
          setManualDetails={setManualDetails}
          isNotRegisteredWithGP={isNotRegisteredWithGP}
          setIsNotRegisteredWithGP={setIsNotRegisteredWithGP}
          handleClearSelectedGP={handleClearSelectedGP}
          handleEditSelectedGP={handleEditSelectedGP}
          profileData={profileData}
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          dependant={dependant}
          setGpCleared={setGpCleared}
          setDependant={setDependant}
        />
      </div>

      {/* Medical History */}
      <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
        <MedicalHistorySection
          userInfo={userInfo}
          setUserInfo={setUserInfo}
          showTitle={true}
          dependant={true}
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => setShowForm(false)}
          className="bg-[#E7CDDB] border-2 border-[#E7CDDB] text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-white transition mr-2"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="bg-[#E7CDDB] border-2 border-[#E7CDDB] text-[#41264B] font-medium px-5 py-1.5 rounded-md hover:bg-white transition"
        >
          {mode === 'edit' ? 'Update Dependant' : 'Save Dependant'}
        </button>
      </div>
    </div>
  )
}

export default DependantForm
